import { useCallback, useState } from 'react';

const STORAGE_KEY = 'wh-gobbo:system-history';
const MAX_ENTRIES = 10;

export interface SystemHistoryEntry {
  solarSystemId: number;
  name: string;
  visitedAt: string;
}

function readHistory(): SystemHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SystemHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

// Browser-session convenience only — a last-10 ring buffer, not account
// data, so it lives in localStorage rather than the server. No server sync,
// no cross-device continuity, on purpose.
export function useSystemHistory() {
  const [history, setHistory] = useState<SystemHistoryEntry[]>(readHistory);

  const push = useCallback((entry: { solarSystemId: number; name: string }) => {
    setHistory((prev) => {
      const next = [{ ...entry, visitedAt: new Date().toISOString() }, ...prev.filter((e) => e.solarSystemId !== entry.solarSystemId)].slice(
        0,
        MAX_ENTRIES,
      );
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable (private browsing, quota, etc.) — history
        // is a convenience, never worth failing the actual fetch for.
      }
      return next;
    });
  }, []);

  return { history, push };
}
